/**
 * AI Scribe V2 — session recovery storage
 *
 * Mirrors the live session to sessionStorage, keyed by appointment, so a refresh
 * or the offline screen (which unmounts the whole tree) can recover the
 * transcript. Per-tab storage is deliberate — a recovered session should not
 * leak into a different tab or survive the tab closing. The snapshot is
 * AES-GCM encrypted (see `scribe-crypto`) so PHI is never at rest in plaintext.
 */

import {
  SCRIBE_SESSION_SNAPSHOT_VERSION,
  SCRIBE_SESSION_STORAGE_PREFIX,
} from "./constants";
import { decryptScribePayload, encryptScribePayload } from "./scribe-crypto";
import { ScribeSessionSnapshot } from "./types";

const keyFor = (sessionKey: string) =>
  `${SCRIBE_SESSION_STORAGE_PREFIX}${sessionKey}`;

/**
 * Writes are async (encryption) and fire on every transcript change, so a slow
 * cipher could resolve after a newer one. This token lets a stale write bow out
 * instead of clobbering fresher data.
 */
const writeSeq = new Map<string, number>();

export const readScribeSnapshot = async (
  sessionKey: string | undefined
): Promise<ScribeSessionSnapshot | null> => {
  if (!sessionKey) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(sessionKey));
    if (!raw) return null;
    const plain = await decryptScribePayload(sessionKey, raw);
    if (!plain) return null;
    const parsed = JSON.parse(plain) as ScribeSessionSnapshot;
    /** A schema bump must not rehydrate a shape this build no longer understands. */
    if (parsed?.version !== SCRIBE_SESSION_SNAPSHOT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeScribeSnapshot = async (
  sessionKey: string | undefined,
  snapshot: ScribeSessionSnapshot
): Promise<void> => {
  if (!sessionKey) return;
  const seq = (writeSeq.get(sessionKey) ?? 0) + 1;
  writeSeq.set(sessionKey, seq);
  try {
    const envelope = await encryptScribePayload(
      sessionKey,
      JSON.stringify(snapshot)
    );
    /** A newer write already superseded this one — drop it. */
    if (writeSeq.get(sessionKey) !== seq) return;
    sessionStorage.setItem(keyFor(sessionKey), envelope);
  } catch {
    /** Quota, serialisation or crypto failures must never break an active session. */
  }
};

export const clearScribeSnapshot = (sessionKey: string | undefined): void => {
  if (!sessionKey) return;
  writeSeq.delete(sessionKey);
  try {
    sessionStorage.removeItem(keyFor(sessionKey));
  } catch {
    /** Nothing to recover from a failed removal. */
  }
};

/** Presence check only — no decryption, so the recovery decision stays synchronous. */
export const hasScribeSnapshot = (sessionKey: string | undefined): boolean =>
  !!sessionKey && sessionStorage.getItem(keyFor(sessionKey)) !== null;
